import React from 'react';
import BigNumber from 'bignumber.js';
import { Copy, CheckCheck } from 'lucide-react';
import InfoHint from '../common/InfoHint';
import { useCopy } from '../../contexts/CopyContext';
import { formatPriceLevel } from '../../utils/pricePrecision';

// Shared exits table for the FULL (website) metric cards — the scale-out ladder.
// Borderless. Allocation is always a % of the position; the resulting quantity is
// shown beside it. Locked / ↩ Stop are shown in both R and $ (the site has room).
const O = { decimalSeparator: '.', groupSeparator: ',', groupSize: 3 };
const bn = (v) => { try { const n = new BigNumber(v); return n.isNaN() ? null : n; } catch { return null; } };
const rr = (v, sign) => { const n = bn(v); if (!n) return '--'; return `${sign && n.gte(0) ? '+' : ''}${n.toFormat(2, O)}R`; };
const money = (v, sign) => { const n = bn(v); if (!n) return '--'; const s = n.lt(0) ? '−' : (sign && n.gt(0) ? '+' : ''); return `${s}$${n.abs().toFormat(2, O)}`; };
const qtyFmt = (v) => { const n = bn(v); if (!n) return '--'; return n.isInteger() ? n.toFormat(0, O) : n.toFormat(Math.min(4, Math.max(2, n.decimalPlaces())), O); };
const writeClipboard = (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) { navigator.clipboard.writeText(text); return; }
  } catch {}
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-999999px';
    document.body.appendChild(ta); ta.focus(); ta.select();
    document.execCommand('copy'); ta.remove();
  } catch {}
};

// Click-to-copy cell — matches the rest of the site: a Copy icon fades in on hover,
// and on click it copies the clean number and shows a green ✓✓ (CheckCheck) for ~1.5s.
// The icon slot is always reserved so the column never shifts. Shared copiedField
// (CopyContext) keeps only one ✓✓ lit at a time, exactly like CopyableValue.
const Cp = ({ copy, label, children }) => {
  const { copiedField, handleCopy } = useCopy();
  const copied = copiedField === label;
  const onClick = (e) => {
    e.stopPropagation();
    const n = bn(copy);
    if (!n) return;
    writeClipboard(n.toString());
    handleCopy(label);
  };
  return (
    <button
      onClick={onClick}
      aria-label={`Copy ${label}`}
      className="group inline-flex items-center gap-1 align-middle cursor-pointer rounded px-0.5 -mx-0.5 hover:bg-white/[0.06] bg-transparent border-none p-0 m-0 focus:outline-none leading-none"
    >
      {children}
      <span className="inline-flex items-center justify-center w-3 h-3 shrink-0">
        {copied
          ? <CheckCheck size={12} className="text-emerald-500" />
          : <Copy size={12} className="opacity-0 group-hover:opacity-100 text-slate-500 transition-opacity" />}
      </span>
    </button>
  );
};

const MultiExitTable = ({ data, pricePrefix = '$', decimals = null }) => {
  if (!data || !Array.isArray(data.rows) || data.rows.length === 0) return null;
  const runner = bn(data.runner);
  const price = (v) => formatPriceLevel(v, decimals, '--');
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-primary-orange uppercase tracking-wider text-sm font-medium inline-flex items-center gap-1">
          Take-Profits
          <InfoHint text="Scale-out plan. Gross R:R = the dirty R the price travels to the optimized target (fees included). Fee is per exit. Net Profit = R/$ locked in by closing through that TP; ↩ Stop = your net R/$ if, after this TP fills, price reverses to the stop on the remainder." />
        </span>
        <div className="h-px flex-1 bg-white/5" />
        <span className="text-slate-300/90 text-sm">{rr(data.blendedNetRR)}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-slate-500 text-[11px] uppercase tracking-wider">
            <th className="text-center font-medium py-1.5 px-2.5">TP</th>
            <th className="text-center font-medium py-1.5 px-2.5">Alloc</th>
            <th className="text-center font-medium py-1.5 px-2.5">Qty</th>
            <th className="text-center font-medium py-1.5 px-2.5">Target</th>
            <th className="text-center font-medium py-1.5 px-2.5">Gross R:R</th>
            <th className="text-center font-medium py-1.5 px-2.5">Fee</th>
            <th colSpan={2} className="text-center font-medium py-1.5 px-2.5">Net Profit</th>
            <th colSpan={2} className="text-center font-medium py-1.5 px-2.5">↩ Stop</th>
          </tr>
        </thead>
        <tbody className="text-slate-300/90">
          {data.rows.map((r, i) => (
            <tr key={i}>
              <td className="text-center py-1.5 px-2.5 text-slate-500">TP{i + 1}</td>
              <td className="text-center py-1.5 px-2.5 text-slate-400">{bn(r.alloc) ? `${bn(r.alloc).toFormat(0, O)}%` : '--'}</td>
              <td className="text-center py-1.5 px-2.5 text-slate-400"><Cp copy={r.qty} label={`tp${i + 1}-qty`}>{qtyFmt(r.qty)}</Cp></td>
              <td className="text-center py-1.5 px-2.5 text-slate-300/90"><Cp copy={r.targetPrice} label={`tp${i + 1}-target`}>{pricePrefix}{price(r.targetPrice)}</Cp></td>
              <td className="text-center py-1.5 px-2.5 text-slate-400"><Cp copy={r.distR} label={`tp${i + 1}-rr`}>{rr(r.distR)}</Cp></td>
              <td className="text-center py-1.5 px-2.5 text-slate-500">{money(r.fee)}</td>
              <td className="text-center py-1.5 pl-2.5 pr-1.5 text-slate-300/90">{rr(r.lockedR, true)}</td>
              <td className="text-center py-1.5 pl-1.5 pr-2.5 text-slate-500">{money(r.lockedMoney, true)}</td>
              <td className="text-center py-1.5 pl-2.5 pr-1.5" style={{ color: bn(r.ifStopR)?.gte(0) ? '#cbd5e1' : '#fb7185' }}>{rr(r.ifStopR, true)}</td>
              <td className="text-center py-1.5 pl-1.5 pr-2.5 text-slate-500">{money(r.ifStopMoney, true)}</td>
            </tr>
          ))}
          {runner && runner.gt(0.0005) && (
            <tr className="text-slate-500">
              <td className="text-center py-1.5 px-2.5">Run</td>
              <td className="text-left py-1.5 px-2.5" colSpan={9}>{runner.times(100).toFormat(0, O)}% rides the remainder</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MultiExitTable;
